import {Component, OnInit, signal} from '@angular/core';
import {UserService} from '../services/user/user.service';
import {CompanyService} from '../services/company.service';
import {ProjectService} from '../services/project.service';
import {User} from '../classes/user.class';
import {Company} from '../classes/company';
import {DatePipe, TitleCasePipe} from '@angular/common';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {CodaltComponent} from '../codalt.component';
import {NgSelectComponent} from '@ng-select/ng-select';
import {MinutesPipe} from '../pipes/minutes.pipe';
import {LocalStorage} from '../storage.class';
import {RegistrationService} from '../services/registration.service';
import {RegistrationV2} from '../classes/registration';
import {Project} from '../classes/project';
import {debounceTime, distinctUntilChanged, forkJoin, merge, of, Subscription} from 'rxjs';
import {DayOfWeekPipe} from '../pipes/day-of-week.pipe';
import {CompanyNamePipe} from '../pipes/company-name.pipe';
import {SumWeekMinutesPipe} from '../pipes/sum-week-minutes.pipe';
import {ReportService} from '../services/report.service';
import {ActivatedRoute, Router} from '@angular/router';
import {PdfReportService} from '../services/pdf-report.service';
import {saveAs} from 'file-saver';
import {MatListOption, MatSelectionList} from '@angular/material/list';
import {ByIdPipe} from '../by-id.pipe';
import {MatTooltip} from '@angular/material/tooltip';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ChangeRegistrationProjectDialogComponent} from './change-registration-project-dialog/change-registration-project-dialog.component';
import {ControlsOf} from '@ngneat/reactive-forms';
import {Title} from '@angular/platform-browser';
import {browserTitle} from '../page-title.strategy';
import {RegistrationsByDayComponent} from '../services/registrations-by-day/registrations-by-day.component';
import {FilterPipe} from '../pipes/filter.pipe';
import {SumPipe} from '../pipes/sum.pipe';
import {AuthorisationService} from '../services/auth/authorisation.service';

interface ReportComment {
    id: number;
    user_id: number;
    description: string;
    datetime: string;
}

interface ReportTotal {
    totaltime: number;
    time: number;
    user_id?: null | number;
    company_id?: null | number;
    work_project_id?: null | number;
}

interface Assignment {
    week: number;
    year: number;
    assignment: string;
    assignment_decimal: number;
    days: {
        date: string,
        totaltime: string,
        totaltime_minutes: number,
    }[];
    totaltime: string;
    totaltime_decimal: number;
}

interface DayUser {
    userId: number;
    name: string;
    totalMinutes: number;
    registrations: ReportRegistration[];
}

type ReportRegistration = RegistrationV2 & { project: Project, comments: ReportComment[], lightStart: boolean, lightEnd: boolean };

export interface FormGroupSelected {
    selectedUser: FormControl<null | number>;
    selectedCompany: FormControl<null | number>;
    selectedYear: FormControl<null | number>;
    selectedPeriod: FormControl<null | number>;
}

@Component({
    selector: 'app-report',
    templateUrl: './report.component.html',
    styleUrls: ['./report.component.scss'],
    imports: [
        ReactiveFormsModule,
        NgSelectComponent,
        MinutesPipe,
        DayOfWeekPipe,
        CompanyNamePipe,
        SumWeekMinutesPipe,
        DatePipe,
        TitleCasePipe,
        MatSelectionList,
        MatListOption,
        ByIdPipe,
        MatTooltip,
        FilterPipe,
        SumPipe,
        RegistrationsByDayComponent
    ]
})
export class ReportComponent extends CodaltComponent implements OnInit {

    hasGlobalReports = <boolean>AuthorisationService.hasFunctionality(this.Functionalities.GlobalReports);
    readonly generatingReport = signal<boolean>(false);

    readonly users = signal<User[]>([]);
    readonly companies = signal<Company[]>([]);
    readonly years = signal<{ value: null | number, name: string }[]>([]);
    readonly periods = signal<{ value: null | number, name: string }[]>([]);

    currentYear = new Date().getFullYear();
    readonly form = signal<FormGroup<ControlsOf<FormGroupSelected>>>(null);
    isPeriodExplicitlySelected = false;

    readonly selectedCompany = signal<Company>(null);
    readonly selectedUser = signal<User>(null);
    readonly projects = signal<Project[]>(null);
    readonly parentProjectIds = signal<number[]>(null);
    readonly registrationsByDay = signal<{ date: string, users: DayUser[] }[]>([]);
    readonly totalsByUser = signal<{ name: string, totalMinutes: number }[]>([]);
    readonly periodTotals = signal<ReportTotal[]>(null);

    readonly filterUsersYeartotals = signal<ReportTotal[]>([]);
    readonly filterCompaniesYeartotals = signal<ReportTotal[]>([]);
    readonly filterUsersTotals = signal<ReportTotal[]>([]);
    readonly filterCompaniesTotals = signal<ReportTotal[]>([]);
    showFilter = true;

    daysWorked: string[] = [];
    private reports: {
        period: { iso_period_year: number, period: number, start: string, end: string },
        registrations: RegistrationV2[],
        comments: ReportComment[]
    } = null;
    readonly registrationsByWeek = signal<Assignment[]>([]);
    readonly periodRange = signal<{ start: string, end: string } | null>(null);
    private fetchSubscription: Subscription | null = null;

    constructor(
        private companyService: CompanyService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private pdfReportService: PdfReportService,
        private projectService: ProjectService,
        private registrationService: RegistrationService,
        private reportService: ReportService,
        private navigator: Router,
        private router: ActivatedRoute,
        private titleService: Title,
        private userService: UserService,
    ) {
        super();
    }

    ngOnInit() {
        // the form is built once from the initial query params; afterwards the form is the source of truth
        // and the url is kept in sync with it (see syncQueryParams)
        const params = this.router.snapshot.queryParams;
        const isPuppeteer = !!params['token'];
        const {year: currentYear, period: currentPeriod} = this.currentIsoPeriod();
        // 'all' (or any non-numeric value) means an explicitly cleared filter
        const parseParam = (key: string, fallback: number | null): number | null =>
            params[key] ? (parseInt(params[key]) || null) : fallback;

        if (params['period']) {
            this.isPeriodExplicitlySelected = true;
        }
        this.form.set(new FormGroup<ControlsOf<FormGroupSelected>>({
            selectedUser: new FormControl(parseParam('userId', isPuppeteer ? null : LocalStorage.getUser().id)),
            selectedCompany: new FormControl(parseParam('companyId', null)),
            selectedYear: new FormControl(parseParam('year', isPuppeteer ? null : currentYear)),
            selectedPeriod: new FormControl(parseParam('period', isPuppeteer ? null : currentPeriod)),
        }));
        this.handleMetaTitle();

        // sidebar totals only depend on year + period, so only re-fetch when those change
        this.subscriptions.add(
            merge(
                this.form()?.controls.selectedYear.valueChanges,
                this.form()?.controls.selectedPeriod.valueChanges,
            )
                .pipe(distinctUntilChanged())
                .subscribe(() => this.filterUsersCompanies())
        );

        this.subscriptions.add(
            merge(
                this.form()?.controls.selectedUser.valueChanges,
                this.form()?.controls.selectedCompany.valueChanges,
                this.form()?.controls.selectedYear.valueChanges,
                this.form()?.controls.selectedPeriod.valueChanges,
            )
                .pipe(debounceTime(50))
                .subscribe(() => {
                    this.fetchReports();
                    this.handleMetaTitle();
                    this.syncQueryParams();
                })
        );

        this.years.set([
            {value: null, name: 'Alle'},
            ...Array.from({length: new Date().getFullYear() - 2014}, (_, i) => ({value: 2015 + i, name: String(2015 + i)}))
        ]);

        this.periods.set([
            {value: null, name: 'Alle'},
            ...Array.from({length: 13}, (_, i) => ({value: i + 1, name: String(i + 1)}))
        ]);

        this.subscriptions.add(
            this.userService.getList().subscribe({
                next: users => {
                    this.users.set(!this.hasGlobalReports ? users.filter(u => u.id === this.form()?.controls.selectedUser.value) : users);
                },
                error: err => console.error(err)
            })
        );
        const getProjectSortKey = (p: Project) => `${p.company_short ?? p.company_id}_${p.name ?? ''}_${p.id}`;

        this.subscriptions.add(
            this.projectService.getAll().subscribe({
                next: projects => {
                    this.projects.set(projects.sort((a, b) => getProjectSortKey(a).localeCompare(getProjectSortKey(b))).map(p => {
                        p.children = p.children.sort((a, b) => getProjectSortKey(a).localeCompare(getProjectSortKey(b)));
                        return p;
                    }));
                    this.filterUsersCompanies();
                    this.fetchReports();
                },
                error: err => console.error(err)
            })
        );

        this.subscriptions.add(
            this.companyService.getCompanies(true).subscribe({
                next: companies => {
                    this.companies.set(companies);
                },
                error: err => console.error(err)
            })
        );
    }

    private syncQueryParams() {
        const controls = this.form()?.controls;
        // null filters are written as 'all' where the default would otherwise be applied on reload
        this.navigator.navigate([], {
            relativeTo: this.router,
            queryParams: {
                userId: controls.selectedUser.value ?? 'all',
                companyId: controls.selectedCompany.value,
                year: controls.selectedYear.value ?? 'all',
                period: controls.selectedPeriod.value ?? 'all',
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    filterUsersCompanies() {
        this.subscriptions.add(
            this.registrationService.getTotals(this.form()?.controls.selectedYear.value, null, null, null, 'user_id').subscribe(userTotals => {
                this.filterUsersYeartotals.set(userTotals);
            })
        );
        const totalUser = this.hasGlobalReports ? null : LocalStorage.getUser()?.id;
        this.subscriptions.add(
            this.registrationService.getTotals(this.form()?.controls.selectedYear.value, null, totalUser, null, 'company_id').subscribe(companyTotals => {
                this.filterCompaniesYeartotals.set(companyTotals);
            })
        );
        this.subscriptions.add(
            this.registrationService.getTotals(this.form()?.controls.selectedYear.value, this.form()?.controls.selectedPeriod.value, null, null, 'user_id').subscribe(userTotals => {
                this.filterUsersTotals.set(userTotals);
            })
        );
        this.subscriptions.add(
            this.registrationService.getTotals(this.form()?.controls.selectedYear.value, this.form()?.controls.selectedPeriod.value, null, null, 'company_id').subscribe(companyTotals => {
                this.filterCompaniesTotals.set(companyTotals);
            })
        );
    }

    fetchReports() {
        const year: number | null = this.form()?.controls.selectedYear.value;
        const userId: number | null = this.form()?.controls.selectedUser.value;
        const companyId: number | null = this.form()?.controls.selectedCompany?.value;
        const period: number | null = this.form()?.controls.selectedPeriod.value;

        this.fetchSubscription?.unsubscribe();

        this.registrationsByDay.set([]);
        this.registrationsByWeek.set([]);
        this.totalsByUser.set([]);
        this.periodTotals.set([]);

        this.daysWorked = [];

        this.fetchSubscription = forkJoin({
            reports: year ? this.registrationService.getDetails(year, period, userId, companyId) : of({period: null, registrations: [], comments: []}),
            periodTotals: this.registrationService.getTotals(year, period, userId, companyId),
            assignments: userId ? this.reportService.getAssignmentReport(userId, year) : of([])
        }).subscribe({
            next: ({reports, periodTotals, assignments}) => {
                this.reports = reports;
                this.periodRange.set(period && reports.period ? {start: reports.period.start, end: reports.period.end} : null);
                this.periodTotals.set(periodTotals as any);
                this.updateFormattedReports(userId, assignments);
            },
            error: err => console.error(err)
        });

        this.subscriptions.add(this.fetchSubscription);
    }


    updateFormattedReports(filteredUserId: number | null = this.form()?.controls.selectedUser.value, assignments = []) {
        if (!this.reports || !this.projects()) {
            return;
        }

        const getParentId = (workProjectId: number): number =>
            this.projects().find(p => p.children?.some(c => c.id === workProjectId))?.id ?? workProjectId;

        // group by day
        const uniqueDates = [...new Set(this.reports.registrations.map(r => r.starttime.substring(0, 10)))].sort();
        const byDay = this.byDay(uniqueDates, this.reports.registrations, this.reports.comments);

        this.registrationsByDay.set(byDay);

        this.totalsByUser.set(
            byDay.flatMap(day => day.users).reduce((acc, user) => {
                const existing = acc.find(u => u.name === user.name);
                existing ? existing.totalMinutes += user.totalMinutes : acc.push({name: user.name, totalMinutes: user.totalMinutes});
                return acc;
            }, [] as { name: string, totalMinutes: number }[])
        );

        this.registrationsByWeek.set(assignments.map(a => ({
            ...a,
            totaltime_decimal: +a.totaltime_decimal,
            assignment_decimal: +(a.assignment_decimal ?? 0),
            days: Object.values<any>(a.days ?? {}).map(day => ({...day, totaltime_minutes: +day.totaltime_minutes}))
        })));

        this.daysWorked = uniqueDates;

        this.parentProjectIds.set([...new Set(this.periodTotals()?.map(t => getParentId(t.work_project_id)))]);

        this.selectedCompany.set(this.companies().find(c => c.id === this.form()?.controls.selectedCompany.value) ?? null);
        this.selectedUser.set(this.users().find(u => u.id === this.form()?.controls.selectedUser.value) ?? null);

        this.handleMetaTitle();
        setTimeout(() => document.body.appendChild(document.createElement("readyforpuppeteer")));
    }


    private byDay(uniqueDates: string[], allRegistrations: RegistrationV2[], allComments: ReportComment[]) {
        return uniqueDates.map(date => {
            const dayRegistrations = allRegistrations.filter(r => r.starttime.startsWith(date));
            const users = [...new Set(dayRegistrations.map(r => r.user_id))].map(userId => {
                const registrations = dayRegistrations.filter(r => r.user_id === userId);
                const sorted = [...registrations].sort((a, b) => a.starttime.localeCompare(b.starttime));
                return {
                    userId,
                    name: this.users().find(u => u.id === userId)?.name ?? `User ${userId}`,
                    totalMinutes: this.minutesFromRegistrations(registrations),
                    registrations: sorted.map((r, i) => ({
                        ...r,
                        project: this.findProject(r.parent_work_project_id) ?? {id: r.parent_work_project_id, company_id: r.company_id} as Project,
                        lightStart: i > 0 && sorted[i - 1].endtime === r.starttime,
                        lightEnd: i < sorted.length - 1 && r.endtime === sorted[i + 1].starttime,
                        comments: allComments
                            .filter(c => c.user_id === userId && c.datetime >= r.starttime && c.datetime < r.endtime)
                            .sort((a, b) => a.datetime.localeCompare(b.datetime))
                    }))
                };
            });
            return {date, users: users.sort((a, b) => a.name.localeCompare(b.name))};
        });
    }

    public downloadReport() {
        const year = this.form()?.controls.selectedYear.value;
        const period = this.form()?.controls.selectedPeriod.value;
        const userId = this.form()?.controls.selectedUser.value;
        const companyId = this.form()?.controls.selectedCompany.value;

        const params = {
            ...(year && {year}),
            ...(period && {period}),
            ...(userId && {userId}),
            ...(companyId && {companyId}),
        };

        const yearLabel = year ?? 'alle_jaren';
        this.generatingReport.set(true);
        this.subscriptions.add(
            this.pdfReportService.getReportPdf(params).subscribe({
                next: document => {
                    this.generatingReport.set(false);
                    saveAs(document, `rapport_${yearLabel}${period ? '_' + period : ''}${userId ? '_' + userId : ''}${companyId ? '_' + companyId : ''}.pdf`);
                },
                error: () => this.generatingReport.set(false)
            })
        );
    }

    private findProject(projectId: number): Project | null {
        for (const project of this.projects()) {
            if (project.id === projectId) return project;
            const child = project.children?.find(c => c.id === projectId);
            if (child) return child;
        }
        return null;
    }

    private minutesFromRegistrations(registrations: RegistrationV2[]): number {
        return registrations.reduce((sum, r) => sum + (new Date(r.endtime).getTime() - new Date(r.starttime).getTime()) / 60000, 0);
    }

    /** Right click on a project line copies its azure id, or the work project id when there is none */
    copyProjectId(event: MouseEvent, project: Project) {
        event.preventDefault();
        const id = project.azure_id ? String(project.azure_id) : `#${project.id}`;
        navigator.clipboard.writeText(id).then(
            () => this.snackBar.open(`${id} gekopieerd`, null, {duration: 2000}),
            () => this.snackBar.open('Kopiëren mislukt', null, {duration: 2000})
        );
    }

    changeRegistrationProjectFn = (registration: ReportRegistration, event: MouseEvent) => {
        this.changeRegistrationProject(registration, event as PointerEvent);
    };

    changeRegistrationProject(registration: ReportRegistration, event: PointerEvent) {
        event.preventDefault();

        const dialogRef = this.dialog.open(ChangeRegistrationProjectDialogComponent, {
            data: {
                registration,
                projects: this.projects() ?? []
            },
            maxWidth: '95vw',
            width: '720px',
            disableClose: true,
            panelClass: 'position-relative'
        });

        this.subscriptions.add(
            dialogRef.afterClosed().subscribe((workProjectId: null | number) => {
                if (!workProjectId || workProjectId === registration.work_project_id) {
                    return;
                }

                this.subscriptions.add(
                    this.registrationService.changeProject(registration.id, workProjectId).subscribe({
                        next: () => this.fetchReports(),
                        error: err => console.error(err)
                    })
                );
            })
        );
    }

    changeSelectedYear(action: string) {
        this.form()?.controls.selectedYear.setValue(action === 'increment' ? this.form()?.controls.selectedYear.value + 1 : this.form()?.controls.selectedYear.value - 1);
    }

    changeSelectedPeriod(action: string) {
        this.isPeriodExplicitlySelected = true;
        this.form()?.controls.selectedPeriod.setValue(action === 'increment' ? this.form()?.controls.selectedPeriod.value + 1 : this.form()?.controls.selectedPeriod.value - 1);
    }

    selectUser(event: MouseEvent, userId: number) {
        if (event.ctrlKey) {
            this.form()?.controls.selectedUser.setValue(this.form()?.controls.selectedUser.value === userId ? null : userId);
            return;
        }
        this.form()?.controls.selectedCompany.setValue(null);
        this.form()?.controls.selectedUser.setValue(userId);
    }

    selectCompany(event: MouseEvent, companyId: number) {
        if (event.ctrlKey) {
            this.form()?.controls.selectedCompany.setValue(this.form()?.controls.selectedCompany.value === companyId ? null : companyId);
            return;
        }
        this.form()?.controls.selectedUser.setValue(null);
        this.form()?.controls.selectedCompany.setValue(companyId);
    }

    private currentIsoPeriod(): { year: number, period: number } {
        const today = new Date();
        const tmp = new Date(today);
        tmp.setDate(tmp.getDate() + 4 - (tmp.getDay() || 7));
        const year = tmp.getFullYear();
        const yearStart = new Date(year, 0, 4);
        yearStart.setDate(yearStart.getDate() - (yearStart.getDay() || 7) + 1);
        const daysSinceStart = Math.floor((today.getTime() - yearStart.getTime()) / 86400000);
        return {year, period: Math.min(Math.floor(daysSinceStart / 28) + 1, 13)};
    }

    getChildProjectTotals(parentProject: Project): ReportTotal[] {
        const childProjectIds = parentProject.children?.map(c => c.id) ?? [];
        return this.periodTotals()?.filter(t => childProjectIds.includes(t.work_project_id)) ?? [];
    }

    getChildProjectsWithTime(parentProject: Project): Project[] {
        return parentProject.children.filter(pc => this.periodTotals().find(pt => pt.work_project_id === pc.id)?.time > 0);
    }

    private handleMetaTitle() {
        const yearTitle = this.form()?.controls.selectedYear.value ? this.form()?.controls.selectedYear.value : '';
        const periodTitle = this.form()?.controls.selectedPeriod.value ? `P${this.form()?.controls.selectedPeriod.value}` : '';
        const companyTitle = this.form()?.controls.selectedCompany.value && this.selectedCompany() ? `${this.selectedCompany().name} - ` : '';
        const userTitle = this.form()?.controls.selectedUser.value && this.selectedUser() ? `${this.selectedUser().name} - ` : '';

        const dateSeparator = this.form()?.controls.selectedYear.value || this.form()?.controls.selectedPeriod.value ? ' - ' : '';

        this.titleService.setTitle(browserTitle(`${yearTitle}${periodTitle}${dateSeparator}${companyTitle}${userTitle}Rapportages`));
    }
}
